import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCart } from '../Context/CartContext'
import { useCurrency } from '../Context/CurrencyContext'
import './CartDrawer.css'

const formatCategory = (category) => {
  if (!category) return ''
  return String(category)
    .replace(/&/g, ' & ')
    .split(/[\s-_]+/)
    .filter(Boolean)
    .map((part) => part.toUpperCase())
    .join(' ')
}

function CartDrawer({ open, onClose, onToast }) {
  const navigate = useNavigate()
  const { cartItems, removeFromCart, updateQuantity, getTotalPrice, getTotalItems } = useCart()
  const { formatPrice, isForeign, currency, baseCurrency } = useCurrency()

  useEffect(() => {
    if (!open) return undefined
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = previous
      window.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  if (!open) return null

  const itemCount = getTotalItems?.() ?? cartItems.reduce((sum, item) => sum + (item.quantity || 1), 0)
  const total = getTotalPrice?.() ?? cartItems.reduce((sum, item) => sum + (Number(item.price || item.priceNew || 0) * (item.quantity || 1)), 0)

  const handleRemove = async (id, name) => {
    await removeFromCart(id)
    onToast?.(`${name || 'Item'} removed from cart`)
  }

  const handleQty = async (id, next) => {
    const value = Math.max(1, next)
    await updateQuantity(id, value)
  }

  const goCheckout = () => {
    onClose?.()
    navigate('/checkout')
  }

  const continueShopping = () => {
    onClose?.()
    navigate('/shop')
  }

  return (
    <div className="cart-drawer-overlay" onClick={onClose} role="presentation">
      <aside
        className="cart-drawer"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cart-drawer-title"
      >
        <header className="cart-drawer__header">
          <div>
            <h4 id="cart-drawer-title" className="cart-drawer__title">Your Cart</h4>
            <p className="cart-drawer__subtitle">
              {itemCount === 0 ? 'No items yet' : `${itemCount} item${itemCount === 1 ? '' : 's'}`}
            </p>
          </div>
          <button type="button" className="cart-drawer__close" onClick={onClose} aria-label="Close cart">
            Close
          </button>
        </header>

        <div className="cart-drawer__body">
          {cartItems.length === 0 ? (
            <div className="cart-drawer__empty">
              <div className="cart-drawer__empty-icon" aria-hidden="true">
                <i className="fa-solid fa-bag-shopping" />
              </div>
              <h5>Your cart is empty</h5>
              <p>Browse the shop and add products to get started.</p>
              <button type="button" className="cart-drawer__btn cart-drawer__btn--primary" onClick={continueShopping}>
                Continue Shopping
              </button>
            </div>
          ) : (
            <ul className="cart-drawer__list">
              {cartItems.map((item, index) => {
                const name = item.name || item.title || 'Product'
                const unitPrice = Number(item.price ?? item.priceNew ?? 0)
                const qty = Math.max(1, Number(item.quantity) || 1)
                const lineTotal = unitPrice * qty

                return (
                  <li key={`${item.id}-${index}`} className="cart-drawer__item">
                    <div className="cart-drawer__thumb">
                      <img
                        src={item.image || '/placeholder-image.png'}
                        alt={name}
                        onError={(e) => {
                          e.target.src = '/placeholder-image.png'
                        }}
                      />
                    </div>

                    <div className="cart-drawer__meta">
                      <div className="cart-drawer__meta-top">
                        <h5 className="cart-drawer__name">{name}</h5>
                        <button
                          type="button"
                          className="cart-drawer__remove"
                          onClick={() => handleRemove(item.id, name)}
                          aria-label={`Remove ${name}`}
                          title="Remove"
                        >
                          <i className="fa-solid fa-xmark" aria-hidden="true" />
                        </button>
                      </div>

                      {item.category && (
                        <span className="cart-drawer__badge">{formatCategory(item.category)}</span>
                      )}

                      <div className="cart-drawer__meta-bottom">
                        <div className="cart-drawer__qty" aria-label={`Quantity for ${name}`}>
                          <button
                            type="button"
                            onClick={() => handleQty(item.id, qty - 1)}
                            disabled={qty <= 1}
                            aria-label="Decrease quantity"
                          >
                            <i className="fa-solid fa-minus" aria-hidden="true" />
                          </button>
                          <span
                            className="cart-drawer__qty-value"
                            style={{ minWidth: `${Math.max(2.5, String(qty).length + 1)}ch` }}
                          >
                            {qty}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleQty(item.id, qty + 1)}
                            aria-label="Increase quantity"
                          >
                            <i className="fa-solid fa-plus" aria-hidden="true" />
                          </button>
                        </div>

                        <div className="cart-drawer__prices">
                          <span className="cart-drawer__unit">{formatPrice(unitPrice)}</span>
                          <strong className="cart-drawer__line">{formatPrice(lineTotal)}</strong>
                        </div>
                      </div>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {cartItems.length > 0 && (
          <footer className="cart-drawer__footer">
            <div className="cart-drawer__total-row">
              <span>Total</span>
              <strong>{formatPrice(total)}</strong>
            </div>
            {isForeign ? (
              <p className="cart-drawer__note">
                Shown in {currency}. Checkout payment is in {baseCurrency}.
              </p>
            ) : (
              <p className="cart-drawer__note">Delivery calculated at checkout</p>
            )}
            <button type="button" className="cart-drawer__btn cart-drawer__btn--primary" onClick={goCheckout}>
              Proceed to Checkout
              <i className="fa-solid fa-arrow-right" aria-hidden="true" />
            </button>
            <button type="button" className="cart-drawer__btn cart-drawer__btn--ghost" onClick={continueShopping}>
              Continue Shopping
            </button>
          </footer>
        )}
      </aside>
    </div>
  )
}

export default CartDrawer
